/**
 * Agent Custodian Knowledge Base — live content from agent-custodian introspection APIs.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, BookOpen, CheckCircle, Lightbulb, Loader2 } from 'lucide-react';
import { useCustodianIntrospection } from '../hooks/useCustodianIntrospection';

export default function KnowledgeBase() {
  const sopsQuery = useCustodianIntrospection('sops');
  const kbQuery = useCustodianIntrospection('knowledge-base');

  const commandments: string[] =
    sopsQuery.data?.sops?.validation_rules?.commandments ??
    sopsQuery.data?.validation_rules?.commandments ??
    [];

  const kb = kbQuery.data?.knowledge_base ?? kbQuery.data ?? {};
  const categories = kb?.categories && typeof kb.categories === 'object' ? kb.categories : {};
  const bestAlways = kb?.best_practices?.always_do ?? [];
  const bestNever = kb?.best_practices?.never_do ?? [];

  const loading = sopsQuery.isLoading || kbQuery.isLoading;
  const error = sopsQuery.isError && kbQuery.isError;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-8 text-white">
        <div className="flex items-center gap-4 mb-4">
          <BookOpen className="w-12 h-12" />
          <div>
            <h1 className="text-4xl font-bold">Agent Custodian Knowledge Base</h1>
            <p className="text-purple-100 text-lg mt-2">
              Live rules and learnings from agent-custodian introspection
            </p>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading knowledge base…
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex gap-2 text-red-800 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          Could not load introspection data. Verify agent-custodian is reachable at
          /cp/api/agent-custodian/introspection/*.
        </div>
      )}

      {commandments.length > 0 && (
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
            <CardTitle className="flex items-center gap-3 text-2xl">
              <CheckCircle className="w-8 h-8 text-purple-600" />
              The Commandments of Agent Building
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ol className="space-y-3">
              {commandments.map((commandment, index) => (
                <li key={index} className="flex gap-4 items-start">
                  <span className="font-bold text-blue-600 text-xl min-w-[32px]">{index + 1}.</span>
                  <span className="text-gray-800 font-medium">{commandment}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {Object.keys(categories).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-600" />
              Knowledge categories
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(categories).map(([key, val]: [string, any]) => (
                <div key={key} className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-gray-900 capitalize mb-2">{key.replace(/_/g, ' ')}</h3>
                  {val?.key_insight && (
                    <p className="text-sm text-gray-700 mb-2">{val.key_insight}</p>
                  )}
                  {Array.isArray(val?.patterns) && (
                    <ul className="text-xs text-gray-600 space-y-1 list-disc pl-4">
                      {val.patterns.slice(0, 5).map((p: string, i: number) => (
                        <li key={i}>{p}</li>
                      ))}
                    </ul>
                  )}
                  {Array.isArray(val?.always_do) && val.always_do.length > 0 && (
                    <div className="mt-2">
                      <Badge variant="outline" className="text-[10px] mb-1">always</Badge>
                      <ul className="text-xs text-gray-600 space-y-1">
                        {val.always_do.slice(0, 4).map((p: string, i: number) => (
                          <li key={i}>✓ {p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {(bestAlways.length > 0 || bestNever.length > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Best practices summary</CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-6">
            {bestAlways.length > 0 && (
              <div>
                <h4 className="font-medium text-green-800 mb-2">Always do</h4>
                <ul className="text-sm space-y-1 text-gray-700">
                  {bestAlways.map((item: string, i: number) => (
                    <li key={i}>✓ {item}</li>
                  ))}
                </ul>
              </div>
            )}
            {bestNever.length > 0 && (
              <div>
                <h4 className="font-medium text-red-800 mb-2">Never do</h4>
                <ul className="text-sm space-y-1 text-gray-700">
                  {bestNever.map((item: string, i: number) => (
                    <li key={i}>✗ {item}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
